import { DayLayoutFunction } from 'react-big-calendar';
import getOverlapStyledEvents from 'react-big-calendar/lib/utils/layout-algorithms/overlap';

interface LayoutStyle {
  top: number;
  height: number | string;
  width: number | string;
  xOffset: number | string;
  left?: number;
}

interface StyledEvent<TEvent extends object> {
  event: TEvent;
  style: LayoutStyle;
  friends: StyledEvent<TEvent>[];
  idx?: number;
  size?: number;
}

const EVENT_PADDING_PX = 3;

// Depth-first search over the overlap graph to find the highest column index
// used by any event transitively connected to the given one.
const getMaxColumnIdx = <TEvent extends object>(
  node: StyledEvent<TEvent>,
  startIdx: number,
  visited: StyledEvent<TEvent>[],
): number => {
  let maxIdx = startIdx;
  for (const friend of node.friends) {
    if (visited.includes(friend)) continue;
    visited.push(friend);
    maxIdx = Math.max(maxIdx, friend.idx as number, getMaxColumnIdx(friend, maxIdx, visited));
  }
  return maxIdx;
};

/**
 * Adaptation of react-big-calendar's built-in 'no-overlap' day layout algorithm
 * (lib/utils/layout-algorithms/no-overlap) that lets us control the horizontal
 * order of simultaneous events. Events that start at the same time are placed
 * left to right following the order returned by `getEventOrder` (lowest first),
 * instead of the library default (longest event first).
 */
export const getOrderedNoOverlapLayout = <TEvent extends object>(
  getEventOrder: (event: TEvent) => number,
): DayLayoutFunction<TEvent> => {
  return (params) => {
    const styledEvents: StyledEvent<TEvent>[] = getOverlapStyledEvents<TEvent>(params).map((styled) => ({
      event: styled.event,
      style: { ...styled.style },
      friends: [],
    }));

    // Column assignment below picks the first free column in array order, so
    // this sort decides who ends up leftmost: earliest start first, then the
    // custom event order, then the longest event.
    styledEvents.sort((a, b) => {
      if (a.style.top !== b.style.top) return a.style.top > b.style.top ? 1 : -1;

      const orderA = getEventOrder(a.event);
      const orderB = getEventOrder(b.event);
      if (orderA !== orderB) return orderA - orderB;

      const heightA = a.style.height as number;
      const heightB = b.style.height as number;
      if (heightA !== heightB) return a.style.top + heightA < b.style.top + heightB ? 1 : -1;
      return 0;
    });

    // Two events are "friends" when their vertical ranges overlap.
    for (let i = 0; i < styledEvents.length - 1; i++) {
      const first = styledEvents[i];
      const firstTop = first.style.top;
      const firstBottom = firstTop + (first.style.height as number);

      for (let j = i + 1; j < styledEvents.length; j++) {
        const second = styledEvents[j];
        const secondTop = second.style.top;
        const secondBottom = secondTop + (second.style.height as number);

        const hasVerticalOverlap =
          (secondTop >= firstTop && secondBottom <= firstBottom) ||
          (secondBottom > firstTop && secondBottom <= firstBottom) ||
          (secondTop >= firstTop && secondTop < firstBottom);

        if (hasVerticalOverlap) {
          first.friends.push(second);
          second.friends.push(first);
        }
      }
    }

    // Assign each event the first column not taken by an already-placed friend.
    for (const styledEvent of styledEvents) {
      const takenColumns = new Set<number>();
      for (const friend of styledEvent.friends) {
        if (friend.idx !== undefined) takenColumns.add(friend.idx);
      }
      let idx = 0;
      while (takenColumns.has(idx)) idx++;
      styledEvent.idx = idx;
    }

    // Every connected group of events shares the same width: 100% divided by
    // the number of columns the group needs.
    for (const styledEvent of styledEvents) {
      if (styledEvent.size) continue;
      const groupFriends: StyledEvent<TEvent>[] = [];
      const maxIdx = getMaxColumnIdx(styledEvent, styledEvent.idx as number, groupFriends);
      const size = 100 / (maxIdx + 1);
      styledEvent.size = size;
      for (const friend of groupFriends) friend.size = size;
    }

    for (const styledEvent of styledEvents) {
      const idx = styledEvent.idx as number;
      let size = styledEvent.size as number;
      styledEvent.style.left = idx * size;

      // Events in the last used column of their row stretch to the right edge.
      const maxFriendIdx = styledEvent.friends.reduce((max, friend) => Math.max(max, friend.idx as number), 0);
      if (maxFriendIdx <= idx) size = 100 - idx * size;

      const padding = idx === 0 ? 0 : EVENT_PADDING_PX;
      styledEvent.style.width = `calc(${size}% - ${padding}px)`;
      styledEvent.style.height = `calc(${styledEvent.style.height}% - 2px)`;
      styledEvent.style.xOffset = `calc(${styledEvent.style.left}% + ${padding}px)`;
    }

    return styledEvents.map(({ event, style }) => ({ event, style })) as ReturnType<DayLayoutFunction<TEvent>>;
  };
};
